import type { ReactNode, Ref } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { Box, Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { Tray } from '@cbhq/cds-web/overlays/tray/Tray';
import { SectionHeader } from '@cbhq/cds-web/section-header/SectionHeader';
import { Text } from '@cbhq/cds-web/typography/Text';

import { EditMemberActivitySummary } from '../components/EditMemberTray/EditMemberActivitySummary';
import type { EditMemberStatus } from '../components/EditMemberTray/EditMemberTrayHeader';
import { EditMemberTrayHeader } from '../components/EditMemberTray/EditMemberTrayHeader';

const messages = defineMessages({
  closeAccessibilityLabel: {
    id: 'Team.editMemberTray.closeAccessibilityLabel',
    defaultMessage: 'Close edit member tray',
    description: 'Accessibility label for the edit member tray close button',
  },
  accessHeading: {
    id: 'Team.editMemberTray.accessHeading',
    defaultMessage: 'Access',
    description: 'Heading for the product access section of the edit member tray',
  },
});

const TRAY_STYLES = {
  container: {
    width: '532px',
  },
  // Sections inside the body manage their own paddingX so the divider can
  // run edge-to-edge per the Figma design.
  content: {
    paddingLeft: 0,
    paddingRight: 0,
  },
} as const;

const EDIT_MEMBER_TRAY_TITLE_ID = 'edit-member-tray-title';

export type EditMemberTrayProps = {
  name: string;
  email: string;
  status: EditMemberStatus;
  joinedLabel?: string;
  lastActiveLabel?: string;
  avatarSrc?: string;
  trayRef: Ref<TrayRefProps>;
  footer?: ReactNode;
  recentActivity?: ReactNode;
  beforeClose: () => void;
  onClose: () => void;
  children: ReactNode;
};

export function EditMemberTray({
  name,
  email,
  status,
  joinedLabel,
  lastActiveLabel,
  avatarSrc,
  trayRef,
  footer,
  recentActivity,
  beforeClose,
  onClose,
  children,
}: EditMemberTrayProps) {
  const { formatMessage } = useIntl();

  return (
    <Tray
      ref={trayRef}
      accessibilityLabel={name}
      accessibilityLabelledBy={EDIT_MEMBER_TRAY_TITLE_ID}
      hideCloseButton
      onCloseComplete={onClose}
      pin="right"
      preventDismiss
      styles={TRAY_STYLES}
      title={
        <HStack width="100%" alignItems="flex-start" gap={2}>
          <EditMemberTrayHeader
            name={name}
            email={email}
            status={status}
            avatarSrc={avatarSrc}
            titleId={EDIT_MEMBER_TRAY_TITLE_ID}
          />
          <IconButton
            transparent
            color="fg"
            name="close"
            onClick={beforeClose}
            accessibilityLabel={formatMessage(messages.closeAccessibilityLabel)}
          />
        </HStack>
      }
      footer={footer}
    >
      <VStack width="100%" gap={3} paddingY={3}>
        <Divider color="bgLine" />
        {joinedLabel || lastActiveLabel ? (
          <Box paddingX={4}>
            <EditMemberActivitySummary
              joinedLabel={joinedLabel}
              lastActiveLabel={lastActiveLabel}
            />
          </Box>
        ) : null}
        <Box paddingX={4}>
          <VStack width="100%" gap={1}>
            <SectionHeader
              title={<Text font="headline">{formatMessage(messages.accessHeading)}</Text>}
              paddingX={0}
              paddingY={0}
            />
            {children}
          </VStack>
        </Box>
        {recentActivity ? (
          <>
            <Divider color="bgLine" />
            {recentActivity}
          </>
        ) : null}
      </VStack>
    </Tray>
  );
}
